import { useEffect, useRef, useSyncExternalStore } from 'react';
import { micReadinessFacade } from '../../configure/host-inputs-facade';
import { captureLogger as logger } from '../../configure/logger-facade';
import { useAudioAnalyzer } from '../../useAudioAnalyzer';
import { useAudioSystem } from '../../useAudioSystem';
import { BarsBase } from './BarsBase';
import { setBarHeight } from './barTransform';
import { getBarHeights } from './getBarHeights';

const FRAME_INTERVAL_MS = 1000 / 30;
// Time-scaled easing matching the pre-throttle 0.2-per-frame at 60 Hz on
// every display refresh rate: the remaining gap decays by 0.8 per 16.7 ms of
// elapsed time. The step is clamped so a backgrounded tab converges over a
// few frames on resume instead of snapping.
const EASING_PER_60HZ_FRAME = 0.2;
const EASING_FRAME_MS = 1000 / 60;
const MAX_EASING_STEP_MS = FRAME_INTERVAL_MS * 3;

interface Props {
  isMic: boolean;
  isTab: boolean;
  isSilent?: boolean;
  className?: string;
  size?: 'default' | 'compact';
  barCount?: 3 | 5;
}

export const Bars = ({
  isMic,
  isTab,
  isSilent,
  className,
  size = 'default',
  barCount = 5,
}: Props) => {
  const barsRef = useRef<HTMLDivElement[]>([]);
  const { getMicAnalyzer, getTabAnalyzer, releaseMicAnalyzer, releaseTabAnalyzer } =
    useAudioAnalyzer();
  const { isReady, errors } = useAudioSystem();
  const audioReady = isReady && !errors.length;
  // Host wiring bridges mic readiness. The server snapshot stays false.
  const readyToRequestMic = useSyncExternalStore(
    micReadinessFacade.subscribe,
    micReadinessFacade.getSnapshot,
    () => false
  );
  const minHeight = size === 'compact' ? 3 : 7.5;
  const heightMultiplier = size === 'compact' ? 0.5 : 1;
  // With 3 bars, render the centre three of the 5 frequency bands so the
  // mid-tall-mid arc and per-band responsiveness are preserved.
  const levelOffset = barCount === 3 ? 1 : 0;

  useEffect(() => {
    let animationFrameId: number;
    let isActive = true;
    let micAnalyzerId: string | undefined;
    let tabAnalyzerId: string | undefined;

    const startAnalyzing = async () => {
      if (!isMic && !isTab) return;
      try {
        let micAnalyzer = null;
        let tabAnalyzer = null;
        let micFrequencyData = new Uint8Array();
        let tabFrequencyData = new Uint8Array();

        if (isMic) {
          const micResult = await getMicAnalyzer();
          micAnalyzerId = micResult.id;
          micAnalyzer = micResult.analyzer;

          micAnalyzer.fftSize = 512;
          micAnalyzer.minDecibels = -115;
          micAnalyzer.maxDecibels = -15;
          micAnalyzer.smoothingTimeConstant = 0.4;

          micFrequencyData = new Uint8Array(micAnalyzer.frequencyBinCount);
        }
        if (isTab) {
          const tabResult = await getTabAnalyzer();
          tabAnalyzerId = tabResult.id;
          tabAnalyzer = tabResult.analyzer;

          tabAnalyzer.fftSize = 512;
          tabAnalyzer.minDecibels = -95;
          tabAnalyzer.maxDecibels = 5;
          tabAnalyzer.smoothingTimeConstant = 0.4;

          tabFrequencyData = new Uint8Array(tabAnalyzer.frequencyBinCount);
        }

        const combinedFrequencyData =
          micAnalyzer && tabAnalyzer
            ? new Uint8Array(Math.max(micFrequencyData.length, tabFrequencyData.length))
            : null;
        const barHeights = new Array<number>(5).fill(5);
        const newHeights = new Array<number>(5).fill(5);
        let lastFrameTime = -FRAME_INTERVAL_MS;

        const updateVisualization = (now: number) => {
          if (!isActive) return;
          animationFrameId = requestAnimationFrame(updateVisualization);
          if (now - lastFrameTime < FRAME_INTERVAL_MS) return;
          const elapsedMs = Math.min(now - lastFrameTime, MAX_EASING_STEP_MS);
          lastFrameTime = now;

          try {
            if (micAnalyzer) micAnalyzer.getByteFrequencyData(micFrequencyData);
            if (tabAnalyzer) tabAnalyzer.getByteFrequencyData(tabFrequencyData);
            if (combinedFrequencyData) {
              for (let i = 0; i < combinedFrequencyData.length; i++) {
                const combinedFrequency =
                  (micFrequencyData[i] ?? 0) + (tabFrequencyData[i] ?? 0);
                combinedFrequencyData[i] =
                  combinedFrequency > 255 ? 255 : combinedFrequency;
              }
            }
            getBarHeights(
              combinedFrequencyData ??
                (micAnalyzer ? micFrequencyData : tabFrequencyData),
              newHeights
            );

            // Easing effect.
            const easing =
              1 - Math.pow(1 - EASING_PER_60HZ_FRAME, elapsedMs / EASING_FRAME_MS);
            for (let i = 0; i < 5; i++) {
              barHeights[i]! += ((newHeights[i] ?? 0) - barHeights[i]!) * easing;
            }

            barsRef.current.forEach((bar, index) => {
              if (!bar) return;
              const height = Math.max(
                (barHeights[index + levelOffset] ?? 0) * heightMultiplier,
                minHeight
              );
              setBarHeight(bar, height, size);
            });
          } catch (error) {
            logger.error('Audio visualization error:', error);
            isActive = false;
            cancelAnimationFrame(animationFrameId);
          }
        };

        animationFrameId = requestAnimationFrame(updateVisualization);
      } catch (error) {
        logger.error('Failed to initialize audio analyzer:', error);
      }
    };

    if (readyToRequestMic) {
      void startAnalyzing();
    }

    return () => {
      isActive = false;
      cancelAnimationFrame(animationFrameId);
      if (micAnalyzerId) void releaseMicAnalyzer(micAnalyzerId);
      if (tabAnalyzerId) void releaseTabAnalyzer(tabAnalyzerId);
    };
  }, [
    getMicAnalyzer,
    getTabAnalyzer,
    isMic,
    isTab,
    readyToRequestMic,
    releaseMicAnalyzer,
    releaseTabAnalyzer,
    heightMultiplier,
    minHeight,
    levelOffset,
    size,
  ]);

  return (
    <BarsBase
      barsRef={barsRef}
      isSilent={isSilent}
      audioReady={audioReady}
      className={className}
      size={size}
      barCount={barCount}
    />
  );
};
